// src/components/receipts/form/index.jsx

import { format } from 'date-fns';
import "./index.css";
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { db } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import SelectDropdown from '../../common/utils/select-dropdown';
import { adjustReceiptTotalPayment, recalcOverdueForBackDate } from '../payment';
import noDataFound from '../../../assets/images/icons/no-data-found.png';
import Skeleton from 'react-loading-skeleton';
import { invoiceOutstanding, fmt } from '../../common/utils/outstanding';
import { round2 } from '../../common/utils/round2';

const N = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

function parseDueDate(d) {
  if (!d) return null;
  if (d.seconds !== undefined) return new Date(d.seconds * 1000);
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt;
}

function parseReceiptDate(d) {
  if (!d) return null;
  if (d.seconds !== undefined) return new Date(d.seconds * 1000);
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt;
}

function getPrincipal(inv) {
  return N(inv.principal) || 0;
}
function getTax(inv) {
  return N(inv.tax) || 0;
}
function getOverdueInterest(inv) {
  return N(inv.overdueInterest) || 0;
}
function getOverdueTax(inv) {
  return N(inv.overdueTax) || 0;
}
function getPrincipalReceived(inv) {
  return N(inv.principalReceived) || 0;
}
function getTaxReceived(inv) {
  return N(inv.taxReceived) || 0;
}
function getInterestReceived(inv) {
  return N(inv.overdueInterestReceived) || 0;
}
function getOverdueTaxReceived(inv) {
  return N(inv.overdueTaxReceived) || 0;
}

const CreateReceipt = ({
  receiptData, setReceiptData,
  selectedParty, setSelectedParty,
  selectedBooking, setSelectedBooking,
  invoices, setInvoices,
  setUpdatedInvoiceIDs,
  inputError, setInputError
}) => {
  const { user } = useAuth();
  const { bid } = user.claims;

  const [isLoading, setIsLoading] = useState(false);
  const [parties, setParties] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [updatedInvoices, setUpdatedInvoices] = useState(null);

  // Payment history state (booking level)
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // Instalment-level history modal state
  const [instHistoryModalOpen, setInstHistoryModalOpen] = useState(false);
  const [instHistoryInvoice, setInstHistoryInvoice] = useState(null);
  const [instHistoryRows, setInstHistoryRows] = useState([]);

  // ---------- Firestore loaders ----------
  async function getInvoices(bookingObj) {
    const { id } = bookingObj;
    const qInv = query(
      collection(db, "Businesses", bid, "Bookings", id, "Invoices")
    );
    const tempData = [];
    const querySnapshot = await getDocs(qInv);
    querySnapshot.forEach((invDoc) => {
      tempData.push({ ...invDoc.data(), id: invDoc.id });
    });

    setInvoices(tempData || []);

    // initial overdue preview based on paymentDate (current or backdated)
    if (receiptData && receiptData.paymentDate) {
      const settings = {
        overdueInterestRate:
          selectedBooking && typeof selectedBooking.overdueInterestRate !== 'undefined'
            ? selectedBooking.overdueInterestRate
            : 0,
        overdueTaxRate:
          selectedBooking && typeof selectedBooking.overdueTaxRate !== 'undefined'
            ? selectedBooking.overdueTaxRate
            : 0
      };
      try {
        const recalculated = recalcOverdueForBackDate(
          tempData,
          receiptData.paymentDate,
          settings
        );
        setUpdatedInvoices(recalculated);
      } catch (err) {
        setUpdatedInvoices(tempData);
      }
    } else {
      setUpdatedInvoices(tempData);
    }
  }

  function getBookings(partyId) {
    const qBookings = query(collection(db, "Businesses", bid, "Bookings"));
    const unsubscribe = onSnapshot(
      qBookings,
      (querySnapshot) => {
        const tempData = [];
        querySnapshot.forEach((doc) => {
          if (doc.data().partyDetails?.id === partyId) {
            tempData.push({ id: doc.id, ...doc.data() });
          }
        });
        setBookings(tempData);
      },
      (error) => {
        console.log(error.message);
      }
    );

    return () => unsubscribe();
  }

  function getParties() {
    setIsLoading(true);
    const qParties = query(
      collection(db, "Businesses", bid, "Parties"),
      orderBy("partyName")
    );
    const unsubscribe = onSnapshot(
      qParties,
      (querySnapshot) => {
        const tempData = [];
        querySnapshot.forEach((doc) => {
          tempData.push({ id: doc.id, ...doc.data() });
        });

        setParties(tempData);
        setIsLoading(false);
      },
      (error) => {
        console.log(error.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }

  // Payment history for selected booking
  const subscribePaymentHistory = (bookingId) => {
    if (!bookingId) {
      setPaymentHistory([]);
      return undefined;
    }

    setIsHistoryLoading(true);

    const receiptsCol = collection(db, "Businesses", bid, "Receipts");
    const qHistory = query(
      receiptsCol,
      where("bookingID", "==", bookingId),
      orderBy("date", "desc")
    );

    const unsubscribe = onSnapshot(
      qHistory,
      (querySnapshot) => {
        const temp = [];
        querySnapshot.forEach((doc) => {
          temp.push({ id: doc.id, ...doc.data() });
        });
        setPaymentHistory(temp);
        setIsHistoryLoading(false);
      },
      (error) => {
        console.error("Error loading payment history:", error.message);
        setIsHistoryLoading(false);
      }
    );

    return () => unsubscribe();
  };

  useEffect(() => {
    const unsub = getParties();
    return () => {
      if (typeof unsub === 'function') unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // prefer preview invoices; else raw invoices
  let filteredInvoicesWithPaymentPlan =
    Array.isArray(updatedInvoices) && updatedInvoices.length
      ? updatedInvoices
      : Array.isArray(invoices) && invoices.length
        ? invoices
        : [];

  filteredInvoicesWithPaymentPlan = filteredInvoicesWithPaymentPlan
    .filter((invItem) => invItem.isCompleted)
    .map((invItem) => invItem);

  // ---------- Instalment-level payment history (modal) ----------
  const openInstalmentHistory = (inv) => {
    if (!inv) return;

    const rows = [];

    if (Array.isArray(paymentHistory) && paymentHistory.length) {
      paymentHistory.forEach((r) => {
        const allocs = Array.isArray(r.invoiceIDs) ? r.invoiceIDs : [];
        const match = allocs.find((a) => a.id === inv.id);
        if (match) {
          const dtObj = parseReceiptDate(r.date || r.paymentDate);
          const totalPaid =
            (Number(match.paidToPrincipal || 0) || 0) +
            (Number(match.paidToTax || 0) || 0) +
            (Number(match.paidToInterest || 0) || 0) +
            (Number(match.paidToOverdueTax || 0) || 0);

          rows.push({
            receiptId: r.id,
            dateObj: dtObj,
            dateStr: dtObj ? dtObj.toLocaleDateString() : "-",
            paymentMode: r.paymentDetails?.paymentMode || r.paymentMode || "-",
            remark: r.description || r.paymentRemark || "-",
            paidPrincipal: Number(match.paidToPrincipal || 0) || 0,
            paidTax: Number(match.paidToTax || 0) || 0,
            paidOverdueInterest: Number(match.paidToInterest || 0) || 0,
            paidOverdueTax: Number(match.paidToOverdueTax || 0) || 0,
            overdueInterestAtPayment:
              Number(match.overdueInterestAtPayment || 0) || 0,
            overdueTaxAtPayment: Number(match.overdueTaxAtPayment || 0) || 0,
            overdueDaysPaid: Number(match.overdueDaysAtPayment || 0) || 0,
            totalPaid
          });
        }
      });

      // sort by date desc
      rows.sort(
        (a, b) => (b.dateObj?.getTime() || 0) - (a.dateObj?.getTime() || 0)
      );
    }

    setInstHistoryInvoice(inv);
    setInstHistoryRows(rows);
    setInstHistoryModalOpen(true);
  };

  const closeInstalmentHistory = () => {
    setInstHistoryModalOpen(false);
    setInstHistoryInvoice(null);
    setInstHistoryRows([]);
  };

  // ---------- Allocation / recalculation ----------
  const adjustPayments = (amount, paymentDateOverride) => {
    // if user cleared the amount, reset preview & allocations
    if (amount === '' || amount === null || typeof amount === 'undefined') {
      setUpdatedInvoiceIDs([]);

      const sourceRaw = Array.isArray(invoices) ? invoices : [];
      if (!sourceRaw.length) {
        setUpdatedInvoices([]);
        setReceiptData((prev) => ({ ...prev, amountReceived: '' }));
        return;
      }

      const paymentDate = paymentDateOverride ?? receiptData?.paymentDate ?? null;
      if (paymentDate) {
        const settings = {
          overdueInterestRate: selectedBooking?.overdueInterestRate ?? 0,
          overdueTaxRate: selectedBooking?.overdueTaxRate ?? 0
        };
        try {
          const recalculated = recalcOverdueForBackDate(
            sourceRaw,
            paymentDate,
            settings
          );
          setUpdatedInvoices(recalculated);
        } catch (err) {
          setUpdatedInvoices(sourceRaw);
        }
      } else {
        setUpdatedInvoices(sourceRaw);
      }

      setReceiptData((prev) => ({ ...prev, amountReceived: '' }));
      return;
    }

    const amt = Number(amount || 0);
    const sourceRaw = Array.isArray(invoices) ? invoices : [];
    if (!sourceRaw.length) return;

    const paymentDate = paymentDateOverride ?? receiptData?.paymentDate ?? null;
    const settings = {
      overdueInterestRate: selectedBooking?.overdueInterestRate ?? 0,
      overdueTaxRate: selectedBooking?.overdueTaxRate ?? 0
    };

    // 1) recalc overdue for CURRENT paymentDate
    const baseWithOverdue = paymentDate
      ? recalcOverdueForBackDate(sourceRaw, paymentDate, settings)
      : sourceRaw;

    // 2) clone, sort by dueDate, and allocate amountPaid across instalments
    const sourceInstalments = baseWithOverdue
      .slice()
      .sort((a, b) => {
        const da = a.dueDate?.seconds
          ? new Date(a.dueDate.seconds * 1000)
          : a.dueDate
            ? new Date(a.dueDate)
            : null;
        const db = b.dueDate?.seconds
          ? new Date(b.dueDate.seconds * 1000)
          : b.dueDate
            ? new Date(b.dueDate)
            : null;

        const ta = da ? da.getTime() : 0;
        const tb = db ? db.getTime() : 0;

        if (ta !== tb) return ta - tb;
        if (a.id && b.id) return String(a.id).localeCompare(String(b.id));
        return 0;
      })
      .map((i) => ({ ...i }));

    const { updatedInstalments, receipt } = adjustReceiptTotalPayment(
      sourceInstalments,
      amt,
      { taxRate: selectedBooking?.taxRate ?? 0 } // GST on principal for reverse calc
    );

    // update UI + invoiceIDs that will be stored in receipt
    setUpdatedInvoiceIDs(receipt.appliedTo || []);
    setUpdatedInvoices(
      Array.isArray(updatedInstalments) ? updatedInstalments : []
    );

    setReceiptData((prev) => ({
      ...prev,
      amountReceived:
        receipt.amountPaid === 0 || isNaN(receipt.amountPaid)
          ? ''
          : receipt.amountPaid
    }));

    return updatedInstalments;
  };

  const totalOutstanding =
    filteredInvoicesWithPaymentPlan.reduce(
      (sum, inv) => sum + invoiceOutstanding(inv),
      0
    ) || 0;

  const totalOverdueInterest =
    filteredInvoicesWithPaymentPlan.reduce((sum, inv) => {
      const overdue = parseFloat(inv.overdueInterest || 0);
      const overdueTax = parseFloat(inv.overdueTax || 0);
      const totalOverdue = parseFloat(overdue + overdueTax);
      return sum + totalOverdue;
    }, 0) || 0;

  const totalAmountPaid =
    filteredInvoicesWithPaymentPlan.reduce((total, item) => {
      const interestReceived = parseFloat(item?.overdueInterestReceived || 0);
      const interestTaxReceived = parseFloat(item?.overdueTaxReceived || 0);
      const principalReceived = parseFloat(item?.principalReceived || 0);
      const taxReceived = parseFloat(item?.taxReceived || 0);
      return (
        total +
        interestReceived +
        interestTaxReceived +
        principalReceived +
        taxReceived
      );
    }, 0) || 0;

  const handlePaymentDateChange = (value) => {
    const dateObj = value ? new Date(value) : null;

    setReceiptData((prev) => ({ ...prev, paymentDate: dateObj }));
    setInputError((prev) => ({ ...prev, paymentDate: '' }));

    const sourceRaw = Array.isArray(invoices) ? invoices : [];
    if (!sourceRaw.length) return;

    if (
      receiptData &&
      receiptData.amountReceived !== '' &&
      receiptData.amountReceived !== null &&
      typeof receiptData.amountReceived !== 'undefined'
    ) {
      adjustPayments(receiptData.amountReceived, dateObj);
    } else {
      const settings = {
        overdueInterestRate: selectedBooking?.overdueInterestRate ?? 0,
        overdueTaxRate: selectedBooking?.overdueTaxRate ?? 0
      };

      try {
        const recalculated = recalcOverdueForBackDate(
          sourceRaw,
          dateObj,
          settings
        );
        setUpdatedInvoices(recalculated);
        setUpdatedInvoiceIDs([]);
      } catch (err) {
        setUpdatedInvoices(sourceRaw);
      }
    }
  };

  // when selectedBooking changes, load payment history
  useEffect(() => {
    if (!selectedBooking || !selectedBooking.id) {
      setPaymentHistory([]);
      return;
    }
    const unsub = subscribePaymentHistory(selectedBooking.id);
    return () => {
      if (typeof unsub === 'function') unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBooking?.id]);

  // when invoices or booking change, re-apply backdate preview + allocations
  useEffect(() => {
    if (selectedBooking && Array.isArray(invoices) && invoices.length) {
      const base = invoices;
      if (receiptData && receiptData.paymentDate) {
        const settings = {
          overdueInterestRate:
            selectedBooking &&
              typeof selectedBooking.overdueInterestRate !== 'undefined'
              ? selectedBooking.overdueInterestRate
              : 0,
          overdueTaxRate:
            selectedBooking &&
              typeof selectedBooking.overdueTaxRate !== 'undefined'
              ? selectedBooking.overdueTaxRate
              : 0
        };
        try {
          const recalculated = recalcOverdueForBackDate(
            base,
            receiptData.paymentDate,
            settings
          );
          setUpdatedInvoices(recalculated);
          // reapply allocation if amount present
          if (
            receiptData &&
            receiptData.amountReceived !== '' &&
            receiptData.amountReceived !== null &&
            typeof receiptData.amountReceived !== 'undefined'
          ) {
            adjustPayments(receiptData.amountReceived);
          } else {
            setUpdatedInvoiceIDs([]);
          }
        } catch (err) {
          setUpdatedInvoices(base);
        }
      } else {
        setUpdatedInvoices(base);
        setUpdatedInvoiceIDs([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, selectedBooking]);

  // ---------- UI ----------
  return (
    <div>
      <div className='row p-3'>
        <div className='col-6'>
          <SelectDropdown
            searchable
            options={parties}
            labelField="partyName"
            valueField="id"
            placeholder="Select Party"
            searchPlaceholder="Search Party"
            selectedValue={
              selectedParty && selectedParty.partyName
                ? selectedParty.partyName
                : ""
            }
            onSelect={(value) => {
              const obj = parties && parties.find((p) => p.id === value);
              setSelectedParty(obj);
              setSelectedBooking(null);
              setBookings(null);
              setInvoices(null);
              setUpdatedInvoices(null);
              setUpdatedInvoiceIDs([]);
              setPaymentHistory([]);
              setReceiptData({
                paymentDate: new Date(),
                paymentRemark: '',
                paymentMode: 'Cash',
                amountReceived: '',
                paymentReferenceNumber: '',
                chequeDate: null,
                paymentReceivedFrom: ''
              });
              getBookings(value);
            }}
            menuClass="shadow"
          />
        </div>
        <div className='col-6'>
          <SelectDropdown
            searchable
            options={bookings}
            labelField="id"
            valueField="id"
            placeholder="Select Booking"
            searchPlaceholder="Search Booking"
            selectedValue={
              selectedBooking && selectedBooking.id ? selectedBooking.id : ''
            }
            onSelect={(value) => {
              const obj = bookings && bookings.find((p) => p.id === value);
              setSelectedBooking(obj);
              setIsLoading(true);
              if (obj) {
                getInvoices(obj).finally(() => setIsLoading(false));
              } else {
                setIsLoading(false);
              }
            }}
            menuClass="shadow"
          />
        </div>
      </div>

      {isLoading ? (
        <div className='bg-light p-5 d-flex align-items-center justify-content-center'>
          <div className='spinner' />
        </div>
      ) : (
        <>
          {filteredInvoicesWithPaymentPlan &&
            filteredInvoicesWithPaymentPlan.length > 0 ? (
            <div className='bg-light p-3'>
              <div className='border rounded overflow-hidden fw-normal'>
                <table className='table mb-0'>
                  <thead className='table-head sticky-top'>
                    <tr>
                      <th className="text-center px-3 bg-table-head fw-normal">
                        <small>S.No.</small>
                      </th>
                      <th className="text-center px-3 bg-table-head fw-normal">
                        <small>Invoice</small>
                      </th>
                      <th className="text-start px-3 bg-table-head fw-normal">
                        <small>Particular</small>
                      </th>
                      <th className="text-end px-3 bg-table-head fw-normal">
                        <small>Installment</small>
                      </th>
                      <th className="text-end px-3 bg-table-head fw-normal">
                        <small>Tax</small>
                      </th>
                      <th className="text-center px-3 bg-table-head fw-normal">
                        <small>Due Date</small>
                      </th>
                      <th className="text-end px-3 bg-table-head fw-normal">
                        <small>Overdue</small>
                      </th>
                      <th className="text-center px-3 bg-table-head fw-normal">
                        <small>Overdue Days</small>
                      </th>
                      <th className="text-end px-3 bg-table-head fw-normal">
                        <small>Amount Received</small>
                      </th>
                      <th className="text-end px-3 bg-table-head fw-normal">
                        <small>Outstanding</small>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoicesWithPaymentPlan.map((inv, index) => {
                      console.log(inv);

                      const dueDt = parseDueDate(inv?.dueDate);
                      const dueDateStr = dueDt
                        ? dueDt.toLocaleDateString()
                        : "-";

                      const principal = getPrincipal(inv);
                      const tax = getTax(inv);

                      const overdueInterest = getOverdueInterest(inv);
                      const overdueTax = getOverdueTax(inv);
                      const totalOverdue = overdueInterest + overdueTax;

                      const principalReceived = getPrincipalReceived(inv);
                      const taxReceived = getTaxReceived(inv);

                      const interestReceived = getInterestReceived(inv);
                      const overdueTaxReceived = getOverdueTaxReceived(inv);

                      const amountReceivedTotal =
                        principalReceived +
                        interestReceived +
                        overdueTaxReceived +
                        taxReceived;

                      const overdueInterestRemaining = Math.max(
                        0,
                        round2(overdueInterest - interestReceived)
                      );
                      const overdueTaxRemaining = Math.max(
                        0,
                        round2(overdueTax - overdueTaxReceived)
                      );
                      const principalRemaining = Math.max(
                        0,
                        round2(principal - principalReceived)
                      );
                      const taxRemaining = Math.max(
                        0,
                        round2(tax - taxReceived)
                      );
                      const outstanding = round2(
                        principalRemaining +
                        taxRemaining +
                        overdueInterestRemaining +
                        overdueTaxRemaining
                      );

                      return (
                        <tr key={inv.id || index} className='border-top'>
                          <td className='px-3 text-center'>
                            {index + 1}.
                          </td>
                          <td className='px-3 text-center'>
                            {inv.id}
                          </td>
                          <td className='px-3 text-start'>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-underline"
                              onClick={() => openInstalmentHistory(inv)}
                            >
                              {inv && inv.name}
                            </button>
                          </td>
                          <td className='px-3 text-end'>
                            {parseFloat(principal.toFixed(2))}
                          </td>
                          <td className='px-3 text-end'>
                            {parseFloat(tax.toFixed(2))}
                          </td>
                          <td className='px-3 text-center'>{dueDateStr}</td>
                          <td className='px-3 text-end text-danger'>
                            {parseFloat(totalOverdue) !== 0
                              ? `${parseFloat(
                                totalOverdue
                              ).toFixed(2)}`
                              : "-"}
                          </td>
                          <td className='px-3 text-center text-danger'>
                            {inv?.overdueDays ?? "-"}
                          </td>
                          <td className='px-3 text-end'>
                            {parseFloat(
                              amountReceivedTotal
                                ? parseFloat(
                                  amountReceivedTotal
                                ).toFixed(2)
                                : 0
                            )}
                          </td>
                          <td className='px-3 text-end'>{fmt(outstanding)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className='no-data-found'>
              <div className='text-center'>
                <img
                  src={noDataFound}
                  alt="no-data-found"
                  className='img-fluid'
                />
                <p className='mt-3'>No Booking Found</p>
              </div>
            </div>
          )}

          {selectedParty && selectedBooking && (
            <div className='row p-3'>
              <div className='col-md-6'>
                <Form.Group className="mb-3" controlId="paymentDate">
                  <Form.Label>Date of Payment *</Form.Label>
                  <Form.Control
                    type="date"
                    onChange={(event) =>
                      handlePaymentDateChange(event.target.value)
                    }
                    className={
                      inputError && inputError.paymentDate
                        ? 'border-danger'
                        : ''
                    }
                    value={
                      receiptData && receiptData.paymentDate
                        ? format(receiptData.paymentDate, 'yyyy-MM-dd')
                        : ''
                    }
                    isInvalid={inputError && inputError.paymentDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {inputError && inputError.paymentDate}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4" controlId="paymentRemark">
                  <Form.Label>Remark (Optional)</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={3}
                    placeholder='Enter payment remark'
                    onChange={(event) =>
                      setReceiptData((prev) => ({
                        ...prev,
                        paymentRemark: event.target.value
                      }))
                    }
                    value={receiptData?.paymentRemark ?? ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="paymentMode">
                  <Form.Label>Mode of Payment *</Form.Label>
                  <Form.Select
                    value={receiptData?.paymentMode ?? ''}
                    onChange={(event) => {
                      setReceiptData((prev) => ({
                        ...prev,
                        paymentMode: event.target.value,
                        paymentReferenceNumber: '',
                        paymentReceivedFrom: null,
                        chequeDate: null
                      }));
                      setInputError((prev) => ({
                        ...prev,
                        paymentMode: '',
                        paymentReferenceNumber: '',
                        paymentReceivedFrom: ''
                      }));
                    }}
                    className={
                      inputError && inputError.paymentMode
                        ? 'border-danger'
                        : ''
                    }
                    isInvalid={inputError && inputError.paymentMode}
                  >
                    <option value=''>Select</option>
                    <option value={'Cash'}>Cash</option>
                    <option value={'Debit Card'}>Debit Card</option>
                    <option value={'Credit Card'}>Credit Card</option>
                    <option value={'Bank'}>Bank</option>
                    <option value={'RTGS/NEFT'}>RTGS/NEFT</option>
                    <option value={'Cheque'}>Cheque</option>
                    <option value={'UPI'}>UPI</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {inputError && inputError.paymentMode}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className='col-md-6 my-4'>
                <div
                  className={
                    inputError && inputError.amountReceived
                      ? 'border border-danger rounded h-100 px-3'
                      : 'border rounded h-100 mt-2 px-3'
                  }
                >
                  <Form.Group className="mb-0">
                    <div className='d-flex align-items-center justify-content-between text-success pt-3'>
                      <div>Total Installment Amount</div>
                      <div className='pe-2'>
                        {fmt(
                          filteredInvoicesWithPaymentPlan.reduce((s, inv) => {
                            const p = getPrincipal(inv);
                            const t = getTax(inv);
                            return s + p + t;
                          }, 0)
                        )}
                      </div>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <div className='d-flex align-items-center justify-content-between text-success pt-3'>
                      <div>Overdue Amount</div>
                      <div className='pe-2'>{fmt(totalOverdueInterest)}</div>
                    </div>
                  </Form.Group>

                  <hr />

                  <Form.Group className="mb-3">
                    <div className='d-flex align-items-center justify-content-between text-success'>
                      <div>Paid Amount</div>
                      <div className='pe-2'>{fmt(totalAmountPaid)}</div>
                    </div>
                  </Form.Group>

                  <div className='d-flex align-items-center justify-content-between py-2 mb-3'>
                    <div className='w-40 h5 mb-0'>Amount</div>
                    <Form.Group controlId="amountReceived">
                      <Form.Control
                        type="number"
                        placeholder='0'
                        onFocus={(e) => e.target.select()}
                        className={
                          inputError && inputError.amountReceived
                            ? 'border-danger text-end'
                            : 'text-end'
                        }
                        onChange={(event) => {
                          const valueRaw = event.target.value;

                          // when cleared -> reset preview & allocations
                          if (valueRaw === '' || valueRaw === null) {
                            setReceiptData((prev) => ({
                              ...prev,
                              amountReceived: ''
                            }));
                            setInputError((prev) => ({
                              ...prev,
                              amountReceived: ''
                            }));
                            adjustPayments('');
                            return;
                          }

                          const value = valueRaw ? parseFloat(valueRaw) : 0;
                          const allowedMax =
                            Math.round(totalOutstanding * 100) / 100;
                          if (value > allowedMax + 0.0001) {
                            setInputError((prev) => ({
                              ...prev,
                              amountReceived:
                                'Amount must be less than or equal to Total Outstanding.'
                            }));
                            setReceiptData((prev) => ({
                              ...prev,
                              amountReceived: event.target.value
                            }));
                          } else {
                            setReceiptData((prev) => ({
                              ...prev,
                              amountReceived: event.target.value
                            }));
                            setInputError((prev) => ({
                              ...prev,
                              amountReceived: ''
                            }));
                            adjustPayments(event.target.value);
                          }
                        }}
                        value={
                          receiptData &&
                            receiptData.amountReceived !== undefined &&
                            receiptData.amountReceived !== null
                            ? receiptData.amountReceived
                            : ''
                        }
                      />
                    </Form.Group>
                  </div>

                  {inputError && inputError.amountReceived ? (
                    <Alert
                      variant='danger'
                      className='py-0 text-center text-danger small mb-2'
                    >
                      <small>{inputError && inputError.amountReceived}</small>
                    </Alert>
                  ) : null}

                  <Form.Group className="mb-3">
                    <div className='d-flex align-items-center justify-content-between text-danger'>
                      <div>Balance Amount</div>
                      <div className='pe-2'>{fmt(totalOutstanding)}</div>
                    </div>
                  </Form.Group>
                </div>
              </div>

              {(receiptData &&
                receiptData.paymentMode &&
                (receiptData.paymentMode === 'Cheque' ||
                  receiptData.paymentMode === 'Debit Card' ||
                  receiptData.paymentMode === 'Credit Card' ||
                  receiptData.paymentMode === 'RTGS/NEFT')) && (
                  <div
                    className={
                      receiptData.paymentMode === 'Cheque'
                        ? 'col-md-6'
                        : 'col-md-12'
                    }
                  >
                    <Form.Group
                      className="mb-3"
                      controlId="paymentReferenceNumber"
                    >
                      <Form.Label>
                        UTR No. (Unique Transaction Reference Number)
                      </Form.Label>
                      <Form.Control
                        placeholder='Enter UTR/Reference No.'
                        onChange={(event) => {
                          setReceiptData({
                            ...receiptData,
                            paymentReferenceNumber: event.target.value
                          });
                          setInputError({
                            ...inputError,
                            paymentReferenceNumber: ''
                          });
                        }}
                        value={
                          receiptData && receiptData.paymentReferenceNumber
                            ? receiptData.paymentReferenceNumber
                            : ''
                        }
                        className={
                          inputError && inputError.paymentReferenceNumber
                            ? 'border-danger'
                            : ''
                        }
                        isInvalid={inputError && inputError.paymentReferenceNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {inputError && inputError.paymentReferenceNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                )}

              {receiptData &&
                receiptData.paymentMode &&
                receiptData.paymentMode === 'Cheque' && (
                  <div className='col-md-6'>
                    <Form.Group className="mb-3" controlId="chequeDate">
                      <Form.Label>Cheque Date</Form.Label>
                      <Form.Control
                        type="date"
                        onChange={(event) =>
                          setReceiptData({
                            ...receiptData,
                            chequeDate: new Date(event.target.value)
                          })
                        }
                        className={'border-right-0'}
                        value={
                          receiptData && receiptData.chequeDate
                            ? format(receiptData.chequeDate, 'yyyy-MM-dd')
                            : ''
                        }
                      />
                    </Form.Group>
                  </div>
                )}

              {receiptData &&
                receiptData.paymentMode &&
                receiptData.paymentMode === 'Bank' && (
                  <div className='col-md-12'>
                    <Form.Group
                      className="mb-3"
                      controlId="paymentReceivedFrom"
                    >
                      <Form.Label>Payment Received from *</Form.Label>
                      <Form.Select
                        value={
                          receiptData && receiptData.paymentReceivedFrom
                            ? JSON.stringify(
                              receiptData.paymentReceivedFrom
                            )
                            : ''
                        }
                        onChange={(event) => {
                          setReceiptData({
                            ...receiptData,
                            paymentReceivedFrom: event.target.value
                              ? JSON.parse(event.target.value)
                              : ''
                          });
                          setInputError({
                            ...inputError,
                            paymentReceivedFrom: ''
                          });
                        }}
                        className={
                          inputError && inputError.paymentReceivedFrom
                            ? 'border-danger'
                            : ''
                        }
                        isInvalid={inputError && inputError.paymentReceivedFrom}
                      >
                        <option value=''>Select</option>
                        {selectedBooking &&
                          selectedBooking.partyDetails &&
                          selectedBooking.partyDetails.bankDetails &&
                          selectedBooking.partyDetails.bankDetails.map(
                            (bk_item, bkIndex) => (
                              <option
                                key={bkIndex}
                                value={JSON.stringify(bk_item)}
                              >
                                {bk_item.accountHolderName} -{' '}
                                {bk_item.accountNumber}
                              </option>
                            )
                          )}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {inputError && inputError.paymentReceivedFrom}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                )}

              {receiptData &&
                receiptData.paymentMode &&
                receiptData.paymentMode === 'UPI' && (
                  <div className='col-md-12'>
                    <Form.Group
                      className="mb-3"
                      controlId="paymentReceivedFrom"
                    >
                      <Form.Label>Payment Received from *</Form.Label>
                      <Form.Select
                        value={
                          receiptData && receiptData.paymentReceivedFrom
                            ? JSON.stringify(
                              receiptData.paymentReceivedFrom
                            )
                            : ''
                        }
                        onChange={(event) => {
                          setReceiptData({
                            ...receiptData,
                            paymentReceivedFrom: event.target.value
                              ? JSON.parse(event.target.value)
                              : ''
                          });
                          setInputError({
                            ...inputError,
                            paymentReceivedFrom: ''
                          });
                        }}
                        className={
                          inputError && inputError.paymentReceivedFrom
                            ? 'border-danger'
                            : ''
                        }
                        isInvalid={inputError && inputError.paymentReceivedFrom}
                      >
                        <option value=''>Select</option>
                        {selectedBooking &&
                          selectedBooking.partyDetails &&
                          selectedBooking.partyDetails.bankDetails &&
                          selectedBooking.partyDetails.bankDetails.map(
                            (bk_item, bkIndex) => (
                              <option
                                key={bkIndex}
                                value={JSON.stringify(bk_item.upi)}
                              >
                                {bk_item.upi}
                              </option>
                            )
                          )}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {inputError && inputError.paymentReceivedFrom}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                )}
            </div>
          )}

          {/* Instalment-level payment history modal */}
          <Modal
            show={instHistoryModalOpen}
            onHide={closeInstalmentHistory}
            size="xl"
            centered
            className='bg-payment-transparent'
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Payment History —{" "}
                {instHistoryInvoice?.name || instHistoryInvoice?.id || "Instalment"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {(!instHistoryRows || instHistoryRows.length === 0) && (
                <div className="text-muted small">
                  No payments have been recorded for this instalment yet.
                </div>
              )}
              {instHistoryRows && instHistoryRows.length > 0 && (
                <div className="border rounded overflow-hidden fw-normal">
                  <table className="table mb-0">
                    <thead className="table-head">
                      <tr>
                        <th className="px-3 bg-table-head fw-normal">
                          <small>Receipt No.</small>
                        </th>
                        <th className="px-3 bg-table-head fw-normal">
                          <small>Date</small>
                        </th>
                        <th className="px-3 bg-table-head fw-normal">
                          <small>Mode</small>
                        </th>
                        <th className="px-3 bg-table-head fw-normal">
                          <small>Remark</small>
                        </th>
                        <th className="px-3 text-end bg-table-head fw-normal">
                          <small>Principal</small>
                        </th>
                        <th className="px-3 text-end bg-table-head fw-normal">
                          <small>Tax</small>
                        </th>
                        <th className="px-3 text-end bg-table-head fw-normal">
                          <small>Overdue Int.</small>
                        </th>
                        <th className="px-3 text-end bg-table-head fw-normal">
                          <small>Overdue Tax</small>
                        </th>
                        <th className="px-3 text-end bg-table-head fw-normal">
                          <small>Total Paid</small>
                        </th>
                        <th className="px-3 text-center bg-table-head fw-normal">
                          <small>Overdue @ Payment</small>
                        </th>
                        <th className="px-3 text-center bg-table-head fw-normal">
                          <small>Overdue Tax @ Payment</small>
                        </th>
                        <th className="px-3 text-center bg-table-head fw-normal">
                          <small>Days Paid</small>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {instHistoryRows.map((row, idx) => {
                        console.log(row);

                        return (
                          <tr key={row.receiptId || idx} className="border-top">
                            <td className="px-3">{row.receiptId}</td>
                            <td className="px-3">{row.dateStr}</td>
                            <td className="px-3">{row.paymentMode}</td>
                            <td className="px-3">
                              <small className="text-muted">{row.remark}</small>
                            </td>
                            <td className="px-3 text-end">
                              {fmt(row.paidPrincipal)}
                            </td>
                            <td className="px-3 text-end">
                              {fmt(row.paidTax)}
                            </td>
                            <td className="px-3 text-end">
                              {fmt(row.paidOverdueInterest)}
                            </td>
                            <td className="px-3 text-end">
                              {fmt(row.paidOverdueTax)}
                            </td>
                            <td className="px-3 text-end">
                              {fmt(row.totalPaid)}
                            </td>
                            <td className="px-3 text-center">
                              {fmt(row.overdueInterestAtPayment)}
                            </td>
                            <td className="px-3 text-center">
                              {fmt(row.overdueTaxAtPayment)}
                            </td>
                            <td className="px-3 text-center">
                              {row.overdueDaysPaid || 0}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Modal.Body>
          </Modal>
        </>
      )}
    </div>
  );
};

export default CreateReceipt;